import type {CanvasEnvironment} from './canvas-environment';
import {ResourceType} from '../../@types/resource-type';
import {Component} from '../../@types/component';
import EventSequenceService from '../../services/event-sequence.service';
import componentService from '../../services/component.service';
import * as CanvasUtils from './node-utils';

/**
 * The subset of "the thing that owns a fault tree" that the shared fault-tree-node modals and a
 * fault-tree canvas need. It lets the same modals edit an Event Sequence's fault tree or a
 * Component's fault tree without knowing which. Every mutation resolves to the refreshed container
 * object (an `EventSequence` or a `Component`) that goes back into the workspace.
 *
 * Structural editing only for now -- undo/redo, tags, callouts, quantification etc. are added as
 * further methods when their Component backends land.
 */
export interface FaultTreeContainerRef {
    /** Workspace object type the refreshed container should be stored under. */
    objectType: ResourceType;
    /** The container's uniqueId -- the prefix new fault-tree-node uniqueIds are built from. */
    uniqueId: string;
    /** `MODE_PROBABILITY` | `MODE_FREQUENCY`. */
    quantificationMode: string;
    /** Whether the current user may edit this container's tree. */
    canEdit: boolean;

    addNode(nodeDto: any): Promise<any>;
    updateNode(nodeDto: any): Promise<any>;
    deleteNode(nodeId: number, preserveChildIds: number[]): Promise<any>;
    reorderChildren(parentNodeId: number, childIds: number[]): Promise<any>;
}

/**
 * The active fault-tree container for a canvas: the Component ref if one was set on the
 * environment, otherwise an Event-Sequence-backed ref that reproduces exactly what the modals
 * used to do inline (per-node fault tree lookup via `events.find(...)`, `EventSequenceService.*`
 * calls, `EVENT_SEQUENCE_TREES` object type).
 */
export function resolveFaultTreeContainer(env: CanvasEnvironment | null): FaultTreeContainerRef | null {
    if (!env) {
        return null;
    }
    if (env.faultTreeContainer) {
        return env.faultTreeContainer;
    }
    const es = env.eventSequence;
    if (!es || !es.group) {
        return null;
    }
    return {
        objectType: ResourceType.EVENT_SEQUENCE_TREES,
        uniqueId: es.uniqueId,
        quantificationMode: es.quantificationMode,
        canEdit: CanvasUtils.canAddOrEditResource(es),
        addNode: (nodeDto: any): Promise<any> => {
            // Which event's fault tree the selected parent node belongs to.
            const parentUniqueId: string = env.selection[0]?.get('data')?.uniqueId ?? '';
            const truncated: string = parentUniqueId.replace(es.uniqueId, '').split('.')[0].slice(-2);
            const faultTree = es.events.find((event) => event.uniqueId === es.uniqueId + truncated);
            return EventSequenceService.addFaultTreeNode(faultTree!.faultTreeId, nodeDto);
        },
        updateNode: (nodeDto: any): Promise<any> => EventSequenceService.updateFaultTreeNode(nodeDto, false),
        deleteNode: (nodeId: number, preserveChildIds: number[]): Promise<any> =>
            EventSequenceService.deleteFaultTreeNode(nodeId, preserveChildIds),
        reorderChildren: (parentNodeId: number, childIds: number[]): Promise<any> =>
            EventSequenceService.reorderFaultTreeNodeChildren(parentNodeId, childIds),
    };
}

/** A fault-tree container backed by a {@link Component} and the Component fault-tree endpoints. */
export function makeComponentFaultTreeContainer(component: Component, canEdit: boolean): FaultTreeContainerRef {
    const componentId: number = component.id;
    return {
        // The canvas route, not the Component resource type. A node modal sets the current
        // workspace object from this after a save, so it has to be the type that resolves to this
        // canvas; naming the resource type here reopens the component as its details form.
        objectType: ResourceType.COMPONENT_TREE,
        uniqueId: component.uniqueId,
        quantificationMode: 'MODE_PROBABILITY',
        canEdit,
        addNode: (nodeDto: any): Promise<Component> => componentService.addFaultTreeNode(componentId, nodeDto),
        updateNode: (nodeDto: any): Promise<Component> => componentService.updateFaultTreeNode(componentId, nodeDto),
        deleteNode: (nodeId: number, preserveChildIds: number[]): Promise<Component> =>
            componentService.deleteFaultTreeNode(componentId, nodeId, preserveChildIds),
        reorderChildren: (parentNodeId: number, childIds: number[]): Promise<Component> =>
            componentService.reorderFaultTreeNodeChildren(componentId, parentNodeId, childIds),
    };
}
